import { Ban, CheckCircle2, ListChecks, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { applicantRequirementsApi } from '../applicantsApi';
import type { ApplicantRequirementListItem } from '../applicantTypes';
import { ApplicantRequirementModal } from '../components/ApplicantRequirementModal';
import { APPLICANT_REQUIREMENTS_PATH, RECRUITMENT_SETTINGS_PATH } from '../recruitmentPaths';

type TabKey = 'info';

const BREADCRUMB: BreadcrumbSegment[] = [
  'Human Resources',
  'Recruitment',
  { label: 'Settings', to: RECRUITMENT_SETTINGS_PATH },
  { label: 'Applicant Requirements', to: APPLICANT_REQUIREMENTS_PATH },
  'Applicant Requirement',
];

/**
 * One applicant requirement. Thin, and that is the point: what it carries that the form cannot is
 * how many applicants have it ticked — what a reader wants before renaming or deleting it.
 */
export function ApplicantRequirementProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [requirement, setRequirement] = useState<ApplicantRequirementListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      applicantRequirementsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setRequirement(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this requirement.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /** An undo — no tick and no password. The server still refuses a name taken meanwhile. */
  async function restore() {
    if (!id) return;

    setRunning(true);
    setError(null);

    try {
      await applicantRequirementsApi.restore(id);
      toast.success('Restored the requirement.');
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this requirement.', 0));
    } finally {
      setRunning(false);
    }
  }

  async function confirmDelete() {
    if (!id) return;

    setRunning(true);
    setActionError(null);

    try {
      await applicantRequirementsApi.remove(id);
      toast.success('Deleted the requirement.');
      setDeleting(false);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('Could not delete this requirement.', 0));
    } finally {
      setRunning(false);
    }
  }

  if (error && !requirement) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={error.isNotFound ? undefined : () => load()}
        />
      </div>
    );
  }

  if (loading && !requirement) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!requirement) return null;

  const ticked = requirement.checkedCount;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={BREADCRUMB} />

      <ProfileHeaderShell
        id="applicant-requirement-profile"
        header={
          <ProfileHeader
            id="applicant-requirement-profile-header"
            icon={<ListChecks className="h-6 w-6" aria-hidden="true" />}
            title={requirement.name}
            meta={[`Submitted by ${ticked} ${ticked === 1 ? 'applicant' : 'applicants'}`]}
            status={
              <StatusText tone={requirement.isActive ? 'active' : 'inactive'}>
                {requirement.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={requirement.createdByName}
            createdAt={requirement.createdAt}
            settings={{
              onHistory: () => setHistoryOpen(true),
              onDelete: () => setDeleting(true),
              deletePermission: 'applicant-requirements.delete',
              deleteDisabledReason: requirement.isActive ? null : 'This requirement is already deleted.',
            }}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="applicant-requirement-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : requirement.isActive ? (
            <ProfileBanner
              id="applicant-requirement-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This requirement is on the initial screening checklist."
            />
          ) : (
            <ProfileBanner
              id="applicant-requirement-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This requirement is deleted. It is no longer on the checklist, and applicants who have it ticked keep the tick."
              action={
                <PermissionGate permission="applicant-requirements.delete">
                  <Button
                    id="applicant-requirement-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={running}
                    loading={running}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="applicant-requirement-profile-details"
          title="Details"
          onEdit={() => setEditing(true)}
          editPermission="applicant-requirements.update"
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: requirement.name },
              { label: 'Description', value: requirement.description },
              { label: 'Requires attachment', value: requirement.requiresAttachment ? 'Yes' : 'No' },
              {
                label: 'Submitted by',
                value: (
                  <span className={`font-mono tabular-nums ${ticked === 0 ? 'text-slate-300' : ''}`}>
                    {ticked} {ticked === 1 ? 'applicant' : 'applicants'}
                  </span>
                ),
              },
            ]}
          />
        </ProfileCard>

        {/* Never editable: a record of what happened. */}
        <ProfileCard id="applicant-requirement-profile-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Updated by', value: requirement.updatedByName },
              { label: 'Updated', value: requirement.updatedAt ? formatDateTime(requirement.updatedAt) : null, mono: true },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <ApplicantRequirementModal
          editing={requirement}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="applicant-requirements"
        entityId={requirement.id}
      />

      {deleting ? (
        <ConfirmDialog
          open
          onClose={() => {
            setDeleting(false);
            setActionError(null);
          }}
          onConfirm={() => void confirmDelete()}
          title="Delete applicant requirement"
          danger
          requireConfirm
          requireCredential
          loading={running}
          id="applicant-requirement-profile-delete-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                "{requirement.name}" stops being offered on the initial screening checklist.
                {ticked > 0
                  ? ` ${ticked} ${ticked === 1 ? 'applicant has' : 'applicants have'} it ticked, and keep the tick.`
                  : ''}
              </p>
              <p>Nothing is erased — a deleted requirement can be restored.</p>
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
